import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Image as ImageIcon,
  Sparkles,
  Loader2,
  Download,
  RefreshCw,
  Check,
  Palette
} from 'lucide-react';
import { aiAPI } from '../../services/api';

const ImageGeneratorModal = ({ isOpen, onClose, onImageGenerated, initialTitle = '', initialCategory = '' }) => {
  const [loading, setLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [title, setTitle] = useState(initialTitle);
  const [category, setCategory] = useState(initialCategory);
  const [description, setDescription] = useState('');
  const [style, setStyle] = useState('photorealistic');
  const [error, setError] = useState(null);

  const styles = [
    { id: 'photorealistic', name: 'Photorealistic', emoji: '📸', desc: 'High-quality realistic photos' },
    { id: 'illustration', name: 'Illustration', emoji: '🎨', desc: 'Digital art style' },
    { id: 'abstract', name: 'Abstract', emoji: '✨', desc: 'Symbolic and artistic' }
  ];

  const categories = [
    'medical', 'education', 'emergency', 'nonprofit', 
    'sports', 'research', 'startup', 'community'
  ];

  const generateImage = async () => {
    if (!title || !category) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await aiAPI.generateCampaignImage(title, category, description, style);
      setGeneratedImage(`data:image/png;base64,${data.image_base64}`);
    } catch (err) {
      console.error('Image generation failed:', err);
      setError('Failed to generate image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const useImage = () => {
    if (generatedImage && onImageGenerated) {
      onImageGenerated(generatedImage);
      onClose();
    }
  };

  const downloadImage = () => {
    if (!generatedImage) return;
    
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `campaign-image-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-background border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-teal-400" />
            AI Image Generator
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Create stunning campaign images with GPT Image AI
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Campaign Title */}
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Campaign Title *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Help Sarah Beat Cancer"
              className="bg-card border-border text-foreground placeholder:text-muted-foreground"
            />
          </div>

          {/* Category */}
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Category *</label>
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-all ${
                    category === cat
                      ? 'bg-teal-500/20 text-teal-400 border border-teal-500/50'
                      : 'bg-card text-muted-foreground border border-border hover:border-border'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Additional Description */}
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Additional Description (Optional)</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe specific elements you want in the image..."
              className="bg-card border-border text-foreground placeholder:text-muted-foreground min-h-[80px]"
            />
          </div>

          {/* Style Selector */}
          <div>
            <label className="text-sm text-muted-foreground mb-2 block flex items-center gap-2">
              <Palette className="w-4 h-4" /> Image Style
            </label>
            <div className="grid grid-cols-3 gap-3">
              {styles.map(s => (
                <button
                  key={s.id}
                  onClick={() => setStyle(s.id)}
                  className={`p-3 rounded-xl text-center transition-all ${
                    style === s.id
                      ? 'bg-teal-500/20 border-2 border-teal-500'
                      : 'bg-card border border-border hover:border-border'
                  }`}
                >
                  <span className="text-2xl mb-1 block">{s.emoji}</span>
                  <span className="text-foreground text-sm font-medium block">{s.name}</span>
                  <span className="text-muted-foreground text-xs">{s.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <Button
            onClick={generateImage}
            disabled={loading || !title || !category}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 py-6"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generating Image (may take up to 60s)...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Generate AI Image
              </>
            )}
          </Button>

          {error && (
            <p className="text-red-400 text-sm text-center">{error}</p>
          )}

          {/* Generated Image Preview */}
          {generatedImage && (
            <div className="space-y-3">
              <div className="relative rounded-xl overflow-hidden border border-border">
                <img
                  src={generatedImage}
                  alt="Generated campaign image"
                  className="w-full h-auto"
                />
                <div className="absolute top-2 right-2 flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={downloadImage}
                    className="bg-card/80 hover:bg-secondary"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={useImage}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Use This Image
                </Button>
                <Button
                  onClick={generateImage}
                  variant="outline"
                  disabled={loading}
                  className="border-border"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          )}

          {/* Info */}
          <p className="text-xs text-muted-foreground text-center">
            Images are generated using GPT Image AI. Generation may take 30-60 seconds.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageGeneratorModal;
